import { html, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_HEALTH_DASHBOARD_INVENTORY } from '../../../../webcomponents.constants';
import { GzGenericWidgetContent } from '../../../../genericDashboard/gzGenericWidget';
import { InventoryInfo, InventoryItem } from '../types';
import { translateFn } from '../../../../utils/translator.helper';
import { ifDefined } from 'lit/directives/if-defined.js';
import { WidgetStateFrontEnd } from '../../../../genericDashboard/gzGenericWidget/types';
import { classMap } from 'lit/directives/class-map.js';

/**
 * @summary Type here the description of this component
 * @tag inventory
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement(WEB_COMPONENT_HEALTH_DASHBOARD_INVENTORY)
export class InventoryTemplateWebComponent extends GzGenericWidgetContent {
  @property({ type: Object, attribute: true, reflect: true })
  payload: { inventory: InventoryInfo };

  changedInfoBoxText = `
    <gz-tooltip isEllipsis tooltipText="${translateFn('You have <b>updates</b> for this widget!')}">
      ${translateFn('You have <b>updates</b> for this widget!')}
    </gz-tooltip>
    <br />
    <gz-tooltip
      isEllipsis
      tooltipText="${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}"
    >
      ${translateFn(
        'To experience the latest design, close the current widget and select the new ones in the Settings panel. Apply your changes and save the view.'
      )}
    </gz-tooltip>
  `;

  strings: Record<string, InventoryItem> = {
    winWorkstations: {
      key: 'winWorkstations',
      name: translateFn('Windows workstations'),
      icon: 'ds-icon-win_workstations',
    },
    winServers: {
      key: 'winServers',
      name: translateFn('Windows servers'),
      icon: 'ds-icon-win_servers',
    },
    macOS: {
      key: 'macOS',
      name: translateFn('macOS'),
      icon: 'ds-icon-MacOS',
    },
    linux: {
      key: 'linux',
      name: translateFn('Linux'),
      icon: 'ds-icon-linux',
    },
    physicalEndpoints: {
      key: 'physicalEndpoints',
      name: translateFn('Physical machines'),
      icon: 'ds-icon-entity_endpoint',
      iconSize: 24,
    },
    virtualMachines: {
      key: 'virtualMachines',
      name: translateFn('Virtual machines'),
      icon: 'ds-icon-virtual_machines',
    },
    securityVirtualAppliances: {
      key: 'securityVirtualAppliances',
      name: translateFn('Security Servers'),
      icon: 'ds-icon-security_servers',
    },
    containers: {
      key: 'containers',
      name: translateFn('Containers'),
      icon: 'ds-icon-containers',
    },
    containerHosts: {
      key: 'containerHosts',
      name: translateFn('Container hosts'),
      icon: 'ds-icon-container_hosts',
    },
  };

  private errorStateResponse: InventoryInfo = {
    winWorkstations: null,
    winServers: null,
    macOS: null,
    linux: null,
    physicalEndpoints: null,
    virtualMachines: null,
  };

  private readonly inventoryItemsOrder = [
    this.strings.winWorkstations.key,
    this.strings.winServers.key,
    this.strings.macOS.key,
    this.strings.linux.key,
    this.strings.physicalEndpoints.key,
    this.strings.virtualMachines.key,
    this.strings.securityVirtualAppliances.key,
    this.strings.containers.key,
    this.strings.containerHosts.key,
  ];

  static get styles() {
    return styles;
  }

  protected renderContent(): TemplateResult {
    return html`<div class="main-container">
      ${this.renderChangedInfoBox(this.changedInfoBoxText)}
      ${this.getItems().map((key: string) => this.renderInventoryItem(this.strings[key]))}
    </div>`;
  }

  private renderInventoryItem(item: InventoryItem): TemplateResult | undefined {
    const classes = this.hasSeparator(item?.key) ? 'has-separator' : nothing;

    const value =
      this.state === WidgetStateFrontEnd.IS_ACTIVE && this.payload.inventory[item?.key] !== undefined
        ? this.payload.inventory[item?.key]
        : undefined;
    return html`<gz-inventory-item
      .value="${value}"
      .label="${ifDefined(item?.name)}"
      .icon="${item?.icon}"
      .iconSize="${ifDefined(item?.iconSize)}"
      class="${classes}"
    ></gz-inventory-item>`;
  }

  protected renderLoader(): TemplateResult {
    const loaderWidths: number[] = [];
    let counter = 0;
    this.getItems().forEach(item => {
      if (this.hasSeparator(item)) {
        loaderWidths.push(counter);
        counter = 0;
      }
      counter += 1;
    });
    loaderWidths.push(counter);
    return html`
      <div class="main-container">${loaderWidths.map((width, index) => this.renderItemLoader(width, index > 0))}</div>
    `;
  }

  private renderItemLoader(flexValue: number, displaySeparator: boolean): TemplateResult {
    const classes = { [`flex-${flexValue}`]: true, 'has-separator': displaySeparator };
    return html`
      <div class="${classMap(classes)}">
        <gz-loader></gz-loader>
      </div>
    `;
  }

  protected renderChangedInfoBox(text?: string): TemplateResult | undefined {
    if (this.state !== WidgetStateFrontEnd.IS_CHANGED) {
      return;
    }
    const percentage = (100 * this.getItemsUntilSeparator().length) / this.getItems().length;
    const style = `width: calc(${percentage}% - 1px)`;
    return html`<div class="changed-info-box" style="${style}">${super.renderChangedInfoBox(text)}</div>`;
  }

  private hasSeparator(key: string): boolean {
    return key === 'physicalEndpoints' || key === 'containerHosts';
  }

  private getItemsUntilSeparator(): string[] {
    const itemsUntilSeparator: string[] = [];
    const items = this.getItems();
    let index = 0;
    while (!this.hasSeparator(items[index])) {
      itemsUntilSeparator.push(items[index]);
      index += 1;
    }
    return itemsUntilSeparator;
  }

  private getItems(): string[] {
    const orderedPayload = this.orderPayloadItems();
    return Object.keys(orderedPayload?.inventory || this.errorStateResponse);
  }

  private orderPayloadItems(): { inventory: InventoryInfo } | undefined {
    if (!this.payload?.inventory) {
      return;
    }

    const orderedPayload = {};
    this.inventoryItemsOrder.forEach(key => {
      if (key in this.payload.inventory) {
        orderedPayload[key] = this.payload.inventory[key];
      }
    });
    return { inventory: orderedPayload as InventoryInfo };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_HEALTH_DASHBOARD_INVENTORY: InventoryTemplateWebComponent;
  }
}
